// FLAGS: personal-data
// Host/secondary role toggle for one expert. `hostDisabled` is what stops an unlicensed expert
// being made a Zoom host — the API enforces it too, this only keeps the UI honest.
import { ExpertRole, SpaceEventType } from "@src/models/spaces";
import { secondaryRoleFor } from "@src/components/spaces/createEventForm";

interface RoleToggleProps {
  role: ExpertRole;
  /** The event type decides the second role: co-host (meeting) or panelist (webinar). */
  eventType: SpaceEventType;
  /** Disables the Host option for experts without a Zoom licence. */
  hostDisabled: boolean;
  onChange: (role: ExpertRole) => void;
}

// Mirrors Zoom's roles. Host = alternative host (can start the session), for both types.
// Co-host (meeting): can run the session but can't start it. Panelist (webinar): can present
// but can't start it.
const HOST_TOOLTIP = "Hosts can start the Zoom session.";
const SECONDARY_TOOLTIP: Record<ExpertRole, string> = {
  host: HOST_TOOLTIP,
  cohost: "Co-hosts help run the meeting but can't start it.",
  panelist:
    "Panelists can present (video, screen share) but can't start the session.",
};

/** Host / Co-host (meeting) or Host / Panelist (webinar) segmented toggle. */
const RoleToggle = ({
  role,
  eventType,
  hostDisabled,
  onChange,
}: RoleToggleProps) => {
  const secondaryRole = secondaryRoleFor(eventType);
  const secondaryLabel = secondaryRole === "cohost" ? "Co-host" : "Panelist";
  const base = "px-3 py-1 text-xs font-medium transition-colors";
  const active = "bg-green-700 text-white";
  const inactive = "bg-white text-gray-700 hover:bg-gray-100";

  return (
    <div className="inline-flex overflow-hidden rounded-full border border-gray-300">
      <button
        type="button"
        disabled={hostDisabled}
        aria-pressed={role === "host"}
        title={hostDisabled ? "No Zoom licence — can't host" : HOST_TOOLTIP}
        className={`${base} ${role === "host" ? active : inactive} disabled:cursor-not-allowed disabled:opacity-40`}
        onClick={() => onChange("host")}
      >
        Host
      </button>
      <button
        type="button"
        aria-pressed={role === secondaryRole}
        title={SECONDARY_TOOLTIP[secondaryRole]}
        className={`${base} ${role === secondaryRole ? active : inactive}`}
        onClick={() => onChange(secondaryRole)}
      >
        {secondaryLabel}
      </button>
    </div>
  );
};

export default RoleToggle;
